import { track } from '@betterment/js-runtime';
import { Box, Button, DropdownMenu } from '@betterment/samba-react';

import { LegacyTransferType, LegalAccountTaxationType } from '../../../graphql/schema';
import { locationAssign } from '../../path-helpers';
import { urlBuilder } from '../../urlBuilder';

export type TransactionButtonsTransactionOption =
  | { __typename: 'CharitableDonationOption'; id: string }
  | { __typename: 'RolloverOption'; id: string }
  | { __typename: 'RothIraConversionOption'; id: string }
  | { __typename: 'TransferOption'; id: string; legacyTransferType: LegacyTransferType }
  | { __typename: 'DeferralSettingsEditOption' | 'WireTransferOption' | 'ExternalTransferOption'; id: string };

type Props = {
  transactionOptions: TransactionButtonsTransactionOption[];
  subAccounts: {
    id: number;
    taxationType: LegalAccountTaxationType;
  }[];
  isCompact?: boolean;
};

export const DepositButton = ({ transactionOptions, subAccounts, isCompact = false }: Props) => {
  if (
    !transactionOptions.some(
      o => o.__typename === 'TransferOption' && o.legacyTransferType === LegacyTransferType.Deposit
    )
  ) {
    return null;
  }

  const onDepositClick = () => {
    if (subAccounts.length === 1) {
      locationAssign(urlBuilder.appDepositSetupDestination.absolute({ subAccountId: subAccounts[0].id }));
    } else {
      locationAssign(urlBuilder.appDepositSetup.absolute());
    }
  };

  return (
    <Button
      css={{ width: { base: '100%', md: 'auto' } }}
      onClick={onDepositClick}
      spacing={isCompact ? 'compact' : 'default'}
    >
      Deposit
    </Button>
  );
};

export const OtherTransactionOptionsMenu = ({ transactionOptions, subAccounts, isCompact = false }: Props) => {
  const rolloverOption = transactionOptions.find(o => o.__typename === 'RolloverOption');

  const items = transactionOptions
    .map((option, index) => {
      const key = `${index}:${option.__typename}`;
      switch (option.__typename) {
        case 'RolloverOption': {
          const url = urlBuilder.getRollovers.absolute();
          return { key, eventName: 'Rollover', url, text: 'Rollover to Betterment' };
        }
        case 'RothIraConversionOption': {
          const subAccountId = subAccounts.find(a => a.taxationType === LegalAccountTaxationType.TraditionalIra)?.id;
          if (!subAccountId) return null;
          const url = urlBuilder.appConvertFundsToRoth.absolute({ subAccountId });
          return { key, eventName: 'ConvertToRoth', url, text: 'Convert funds to Roth' };
        }
        case 'CharitableDonationOption':
          return {
            key,
            eventName: 'Charity',
            url: urlBuilder.appCharitableGiftStart.absolute(),
            text: 'Donate to charity',
          };
        case 'TransferOption': {
          const subAccountId = subAccounts.length === 1 ? subAccounts[0].id : undefined;
          switch (option.legacyTransferType) {
            case LegacyTransferType.Transfer: {
              const url = subAccountId
                ? urlBuilder.showSubAccountTransferSetup.absolute({ subAccountId })
                : urlBuilder.showTransferSetup.absolute();
              return { key, eventName: 'GoalTransfer', url, text: 'Transfer within Betterment' };
            }
            case LegacyTransferType.Withdrawal: {
              const url = subAccountId
                ? urlBuilder.appWithdrawalSetup.absolute({ subAccountId })
                : urlBuilder.appWithdrawalSetup.absolute();
              return { key, eventName: 'Withdraw', url, text: 'Withdraw' };
            }
            default:
              return null;
          }
        }
        default:
          return null;
      }
    })
    .filter(i => i !== null);

  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild={true}>
        <Button
          variant="solidSecondary"
          css={{ textWrap: 'nowrap', width: { base: '100%', md: 'auto' } }}
          spacing={isCompact ? 'compact' : 'default'}
        >
          {rolloverOption && !isCompact ? 'Transfer or rollover' : 'Transfer'}
          <Button.Icon name="CaretDown" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {items.map(({ key, eventName, url, text }) => (
          <Box key={key}>
            <DropdownMenu.Item
              onClick={() => {
                track('ElementClicked', { module: 'DropdownMenu', name: eventName });
                locationAssign(url);
              }}
            >
              {text}
            </DropdownMenu.Item>
          </Box>
        ))}
        <DropdownMenu.Separator css={{ my: 'space.100' }} />
        <DropdownMenu.Item
          onClick={() => {
            track('ElementClicked', { module: 'DropdownMenu', name: 'TransferCenter' });
            locationAssign(urlBuilder.getPendingTransfers.absolute());
          }}
        >
          View transfers
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
